import * as React from "react";
import { Check, ChevronDown, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface MultiRoleOption {
  value: string;
  label: string;
}

export interface MultiRoleSelectProps {
  options: MultiRoleOption[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  hideSelectedOptions?: boolean;
  disabled?: boolean;
  className?: string;
}

function toggleValue(values: string[], value: string): string[] {
  if (values.includes(value)) return values.filter(v => v !== value);
  return [...values, value];
}

export function MultiRoleSelect({
  options,
  value,
  onChange,
  placeholder = "Select roles…",
  hideSelectedOptions = false,
  disabled,
  className,
}: MultiRoleSelectProps) {
  const [open, setOpen] = React.useState(false);

  const selected = React.useMemo(() => {
    const optionMap = new Map(options.map(o => [o.value, o.label]));
    return value.map(v => ({ value: v, label: optionMap.get(v) || v }));
  }, [options, value]);

  const handleRemove = (roleValue: string) => {
    onChange(value.filter(v => v !== roleValue));
  };

  const handleToggle = (roleValue: string) => {
    onChange(toggleValue(value, roleValue));
  };

  const visibleOptions = React.useMemo(() => {
    if (!hideSelectedOptions) return options;
    const selected = new Set(value);
    return options.filter(o => !selected.has(o.value));
  }, [hideSelectedOptions, options, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "h-auto min-h-10 w-full justify-between px-2 py-2 font-normal",
            value.length === 0 && "text-muted-foreground",
            className
          )}
        >
          <div className="flex flex-wrap gap-2 items-center">
            {selected.length === 0 ? (
              <span className="text-sm">{placeholder}</span>
            ) : (
              selected.map(s => (
                <Badge key={s.value} variant="secondary" className="rounded-md px-2 py-1">
                  <span className="mr-1">{s.label}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    className="inline-flex items-center justify-center rounded-sm hover:bg-black/10 cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemove(s.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemove(s.value);
                      }
                    }}
                    aria-label={`Remove ${s.label}`}
                  >
                    <X className="h-3 w-3" />
                  </span>
                </Badge>
              ))
            )}
          </div>
          <ChevronDown className="h-4 w-4 opacity-60 ml-2 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]" align="start">
        <Command>
          <CommandInput placeholder="Type a role name…" />
          <CommandList className="max-h-64">
            <CommandEmpty>No roles found.</CommandEmpty>
            <CommandGroup>
              {visibleOptions.map(opt => {
                const isSelected = value.includes(opt.value);
                return (
                  <CommandItem
                    key={opt.value}
                    value={`${opt.label} ${opt.value}`}
                    onSelect={() => handleToggle(opt.value)}
                    className="cursor-pointer"
                  >
                    {!hideSelectedOptions && (
                      <Check className={cn("mr-2 h-4 w-4", isSelected ? "opacity-100" : "opacity-0")} />
                    )}
                    <span>{opt.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
